import { Autocomplete, Chip, TextField } from '@mui/material';
import type { FormikProps } from 'formik';

type Props<ID, T> = {
  disabled?: boolean;
  formik: FormikProps<T>;
  helperText?: string;
  id: ID;
  label?: string;
  placeholder?: string;
  transform?: (value: string) => string;
};

/**
 * Free text entry of a list of values, displayed as chips.
 */
export function FormikChipList<
  ID extends string,
  T extends { [key in ID]?: string[] },
>(props: Props<ID, T>): JSX.Element {
  const { disabled, formik, helperText, id, label, placeholder, transform } =
    props;
  const value = (formik.values[id] as string[] | undefined) ?? [];

  return (
    <Autocomplete<string, true, false, true>
      disabled={formik.isSubmitting || disabled}
      freeSolo
      multiple
      onChange={(_, newValue) => {
        const cleaned = newValue
          .map((item) => (transform ? transform(item) : item).trim())
          .filter((item, index, all) => !!item && all.indexOf(item) === index);
        formik.setFieldValue(id, cleaned);
      }}
      options={[]}
      renderInput={(params) => (
        <TextField
          {...params}
          error={formik.touched[id] && Boolean(formik.errors[id])}
          helperText={(formik.errors[id] as string) || helperText}
          label={label}
          placeholder={placeholder}
        />
      )}
      renderTags={(tags, getTagProps) =>
        tags.map((tag, index) => (
          // biome-ignore lint/correctness/useJsxKeyInIterable: key is provided by getTagProps
          <Chip label={tag} {...getTagProps({ index })} />
        ))
      }
      value={value}
    />
  );
}
